import React, { useEffect, useState } from 'react';
import './Stock.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

const excludedColumns = ["id", "Client_id"];

// Une colonne est "numérique" si toutes ses valeurs renseignées sont des nombres (police mono ; centrée, cf. <style> du rendu)
const estNumerique = (v) => v !== null && v !== '' && v !== undefined && !isNaN(Number(v));

const IconeLoupe = () => (
  <svg className="st-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
  </svg>
);

const Stock = () => {

  useEffect(() => {
    document.title = 'MYSSA – Base stock';
  }, []);

  const [stockData, setStockData]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filterFamille, setFilterFamille] = useState('');
  const [filterItem, setFilterItem]       = useState('');
  const [filterDepot, setFilterDepot]     = useState('');
  const [filterLibelle, setFilterLibelle] = useState('');

  useEffect(() => {
    fetch(`${API}/base_de_donne_stock`)
      .then(r => r.json())
      .then(d => { setStockData(d); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, []);

  const resetFilters = () => {
    setFilterFamille(''); setFilterItem('');
    setFilterDepot('');   setFilterLibelle('');
  };

  const filteredData = stockData.filter(row =>
    (filterFamille === '' || row["Famille produit"]?.toLowerCase().includes(filterFamille.toLowerCase())) &&
    (filterItem    === '' || row["Item"]?.toLowerCase().includes(filterItem.toLowerCase())) &&
    (filterDepot   === '' || row["Nom Dépôt"]?.toLowerCase().includes(filterDepot.toLowerCase())) &&
    (filterLibelle === '' || row["Description"]?.toLowerCase().includes(filterLibelle.toLowerCase()))
  );

  // Couples Dépôt + Emplacement présents plusieurs fois
  const coupleMap = {};
  stockData.forEach(row => {
    const key = `${row["Nom Dépôt"] ?? ''}-${row["Emplacement"] ?? ''}`;
    coupleMap[key] = (coupleMap[key] || 0) + 1;
  });
  const duplicateCouples = new Set(
    Object.entries(coupleMap).filter(([, v]) => v > 1).map(([k]) => k)
  );

  // ── Export Excel (inchangé) ───────────────────────────────────────────────
  const exportToExcel = () => {
    const exportRows = filteredData.map(row =>
      Object.fromEntries(
        Object.entries(row).filter(([k]) => !excludedColumns.includes(k))
      )
    );
    const ws  = XLSX.utils.json_to_sheet(exportRows);
    const wb  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const date = new Date().toISOString().slice(0, 10);
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `stock_${date}.xlsx`);
  };

  const columns = stockData.length > 0
    ? Object.keys(stockData[0]).filter(k => !excludedColumns.includes(k))
    : [];

  const echantillon = stockData.slice(0, 50);
  const colonnesNum = new Set(
    columns.filter(k => {
      const vals = echantillon.map(r => r[k]).filter(v => v !== null && v !== undefined && v !== '');
      return vals.length > 0 && vals.every(estNumerique) && !/emplacement|item|code|n°|numero|numéro/i.test(k);
    })
  );

  const filtres = [
    { label: 'Famille produit', placeholder: 'Famille produit…', value: filterFamille, setter: setFilterFamille },
    { label: 'Item',            placeholder: 'Item…',            value: filterItem,    setter: setFilterItem },
    { label: 'Nom Dépôt',       placeholder: 'Nom Dépôt…',       value: filterDepot,   setter: setFilterDepot },
    { label: 'Description',     placeholder: 'Description…',     value: filterLibelle, setter: setFilterLibelle },
  ];
  const nbFiltresActifs = filtres.filter(f => f.value).length;

  const nbDoublonsAffiches = filteredData.filter(row =>
    duplicateCouples.has(`${row["Nom Dépôt"] ?? ''}-${row["Emplacement"] ?? ''}`)
  ).length;

  return (
    <div className="st-page">
      {/* ★ En-têtes et données centrés dans le tableau (prioritaire sur Stock.css,
            y compris la classe "num" qui aligne les nombres à droite) */}
      <style>{`
        .st-table th, .st-table th.num,
        .st-table td, .st-table td.num { text-align: center !important; vertical-align: middle; }
      `}</style>
      <div className="st-container">

        {/* ── En-tête ── */}
        <div className="st-header">
          <div className="st-title-row">
            <span className="st-accent" />
            <h1 className="st-title">Stock</h1>
            <span className="st-chip-dept">LOG</span>
          </div>
       
        </div>

        {/* ── Filtres ── */}
        <div className="st-filters">
          {filtres.map(({ label, placeholder, value, setter }) => (
            <div key={label} className="st-search">
              <IconeLoupe />
              <input
                type="text"
                className={`st-input ${value ? 'active' : ''}`}
                placeholder={placeholder}
                aria-label={`Filtrer par ${label}`}
                value={value}
                onChange={e => setter(e.target.value)}
              />
              {value && (
                <button type="button" className="st-clear" aria-label={`Effacer ${label}`} onClick={() => setter('')}>×</button>
              )}
            </div>
          ))}

          <button type="button" className="st-btn st-btn-ghost" onClick={resetFilters} disabled={nbFiltresActifs === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
            Réinitialiser{nbFiltresActifs ? ` (${nbFiltresActifs})` : ''}
          </button>

          <button type="button" className="st-btn st-btn-primary" onClick={exportToExcel} disabled={filteredData.length === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
            Export Excel
          </button>
        </div>

        {/* ── Tableau ── */}
        <div className="st-card st-table-card">
          <div className="st-table-head">
            <div className="st-table-head-left">
              <span className="st-table-title">Base stock</span>
              <span className="st-count">
                {filteredData.length}
                {filteredData.length !== stockData.length && <span className="st-count-total"> / {stockData.length}</span>}
              </span>
            </div>
            {nbDoublonsAffiches > 0 && (
              <div className="st-table-head-right">
                <span className="st-legend-dup">
                  <span className="st-legend-swatch" />
                  Emplacement partagé · {nbDoublonsAffiches} ligne{nbDoublonsAffiches > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          <div className="st-table-scroll">
            {loading ? (
              <div className="st-empty">Chargement…</div>
            ) : filteredData.length === 0 ? (
              <div className="st-empty">
                {stockData.length === 0 ? 'Aucune donnée de stock.' : 'Aucune ligne ne correspond aux filtres.'}
              </div>
            ) : (
              <table className="st-table">
                <thead>
                  <tr>
                    {columns.map(k => <th key={k} className={colonnesNum.has(k) ? 'num' : ''}>{k}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, index) => {
                    const key         = `${row["Nom Dépôt"] ?? ''}-${row["Emplacement"] ?? ''}`;
                    const isDuplicate = duplicateCouples.has(key);
                    return (
                      <tr key={index} className={isDuplicate ? 'st-row-dup' : ''}>
                        {columns.map((k, i) => {
                          const surligne = isDuplicate && (k === "Nom Dépôt" || k === "Emplacement");
                          const classes = [
                            colonnesNum.has(k) ? 'num st-mono' : '',
                            k === 'Item' ? 'st-strong st-mono' : '',
                            k === 'Emplacement' ? 'st-mono' : '',
                            k === 'Description' ? 'st-desc' : '',
                            surligne ? 'st-cell-dup' : '',
                          ].filter(Boolean).join(' ');
                          return <td key={i} className={classes}>{row[k]}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stock;